'use strict';

import { Mesh, ShaderMaterial, Vector2 } from 'three';
import vertex from './vertex.glsl';
import fragment from './fragment.glsl';
import { projectMode } from '../../../../settings';
import {Cloudflare} from "../../../api/Cloudflare";

export class Sea {
    #active = config.features.liteMode.settings.sea?.active;
    #url = null;
    #loader = null;
    #dayTexture = null;
    #nightTexture = null;
    #depthTexture = null;
    #mesh = null;
    #material = null;
    #ready = false;
    #speed = config.features.liteMode.settings.sea.speed || 1.0;
    #scale = config.features.liteMode.settings.sea.scale || 1.0;
    #horizon = config.features.liteMode.settings.sea.horizon || 0.5;
    #direction = config.features.liteMode.settings.sea.direction || 1.57;
    #dark = config.features.liteMode.settings.sea.dark || 0.0;
    #light = config.features.liteMode.settings.sea.light || 0.0;
    #cover = config.features.liteMode.settings.sea.cover || 0.0;
    #alpha = config.features.liteMode.settings.sea.alpha || 0.0;
    #tint = config.features.liteMode.settings.sea.tint || 0.0;
    #minWave = config.features.liteMode.settings.sea.minWave || 0.0;
    #maxWave = config.features.liteMode.settings.sea.maxWave || 1.0;


    constructor (url,dayTexture,nightTexture,loader,depthTexture) {
        this.#url = `${url}${config.features.liteMode.settings.sea?.path}?v=${Cloudflare.version}` || '';
        this.#dayTexture = dayTexture;
        this.#nightTexture = nightTexture;
        this.#depthTexture = depthTexture;
        this.#loader = loader;

        return this;
    }

     create(){
        if (!this.#active) return new Promise(resolve => resolve('disabled'));

        return new Promise(resolve => {

            this.#loader.load(this.#url, model => {
                model.traverse(el => {
                    if (el.isMesh) {
                        const geometry = el.geometry.clone();
                        this.#material = new ShaderMaterial({
                            vertexShader: vertex,
                            fragmentShader: fragment,
                            uniforms: {
                                seaTexture: {
                                    value: this.#dayTexture
                                },
                                nightSeaTexture: {
                                    value: this.#nightTexture
                                },
                                depthTexture: {
                                    value: this.#depthTexture
                                },
                                uTime: {
                                    value: 0.0
                                },
                                uTimesOfDay: {
                                    value: 0.0
                                },
                                uSpeed: {
                                    value: this.#speed
                                },
                                uScale: {
                                    value: this.#scale
                                },
                                uHorizon: {
                                    value: this.#horizon
                                },
                                uDirection: {
                                    value: this.#direction
                                },
                                uDark: {
                                    value: this.#dark
                                },
                                uLight: {
                                    value: this.#light
                                },
                                uCover: {
                                    value: this.#cover
                                },
                                uAlpha: {
                                    value: this.#alpha
                                },
                                uTint: {
                                    value: this.#tint
                                },
                                uMinWave: {
                                    value: this.#minWave
                                },
                                uMaxWave: {
                                    value: this.#maxWave
                                },
                                uDepthMouse: {
                                    value: new Vector2()
                                }
                            },
                            depthTest: false,
                            depthWrite: false,
                            transparent: true,
                            opacity: 1
                        });

                        this.#mesh = new Mesh(geometry,this.#material);
                        this.#mesh.name = el.name;
                        this.#mesh.scale.copy(el.scale);
                        this.#mesh.position.set(0,0,-0.2);
                        //this.#mesh.position.copy(el.position);
                        //this.#mesh.rotation.copy(el.rotation);
                        this.#ready = true;
                        this.#debug();
                        resolve(this.#mesh);
                    }
                });
            });

        });

    }

    updateDepthPosition(vec2) {
        this.#material?.uniforms.uDepthMouse.value.set(vec2.x,vec2.y);
    }

    updateTime(time) {
        if (this.#ready) {
            this.#material.uniforms.uTime.value += time;
        }
    }

    updateTimesOfDay(time){
        if (this.#ready) {
            this.#material.uniforms.uTimesOfDay.value = time;
        }
    }

    switcher(state) {
        if (this.#ready) {
            switch (state) {
                case 'show':
                    this.#mesh.visible = true;
                    break;
                case 'hide':
                    this.#mesh.visible = false;
                    break;

            }
        }
    }

    #debug() {
        if (!projectMode.LiteMode.debug) return;

        const params = {
            scale: this.#scale,
            dark: this.#dark,
            light: this.#light,
            cover: this.#cover,
            alpha: this.#alpha,
            tint: this.#tint,
            horizon: this.#horizon,
            direction: this.#direction,
            speed: this.#speed,
            minWave: this.#minWave,
            maxWave: this.#maxWave
        };


        const folder = projectMode.LiteMode.debugFolder.addFolder('Sea');

        folder.add(params,'scale', 0.0, 10000.0, 1).onChange(value => {
            this.#material.uniforms.uScale.value = value;
        });

        folder.add(params,'dark', 0.0, 5.0, 0.001).onChange(value => {
            this.#material.uniforms.uDark.value = value;
        });

        folder.add(params,'minWave', -50, 50, 0.001).onChange(value => {
            this.#material.uniforms.uMinWave.value = value;
        });

        folder.add(params,'maxWave', -50, 50, 0.001).onChange(value => {
            this.#material.uniforms.uMaxWave.value = value;
        });

        folder.add(params,'light', 0.0, 5.0, 0.001).onChange(value => {
            this.#material.uniforms.uLight.value = value;
        });

        folder.add(params,'cover', 0, 5.0, 0.001).onChange(value => {
            this.#material.uniforms.uCover.value = value;
        });

        folder.add(params,'alpha', 0.0, 10.0, 0.001).onChange(value => {
            this.#material.uniforms.uAlpha.value = value;
        });

        folder.add(params,'tint', -2.0, 2.0, 0.001).onChange(value => {
            this.#material.uniforms.uTint.value = value;
        });

        folder.add(params,'horizon', -2.0, 2.0, 0.001).onChange(value => {
            this.#material.uniforms.uHorizon.value = value;
        });

        folder.add(params,'direction', -Math.PI, Math.PI, 0.001).onChange(value => {
            this.#material.uniforms.uDirection.value = value;
        });

        folder.add(params,'speed', 0.0, 1.0, 0.001).onChange(value => {
            this.#material.uniforms.uSpeed.value = value;
        });
    }
}